import { useCallback, useEffect, useState } from 'react';
import { Alert, Button } from '@nimbus-ds/components';
import { useNavegate } from 'App/hooks';
import { useGetPartnerAdminAccessQuery } from 'domains/Configurations/Users/usersApi';
import useTranslationConfigurations from 'domains/Configurations/useTranslationConfigurations';

interface PartnerAdminAlertProps {
  showDetailsButton?: boolean;
}

function PartnerAdminAlert({
  showDetailsButton = false,
}: Readonly<PartnerAdminAlertProps>) {
  const t = useTranslationConfigurations('notifications.partnerAdminAlert');
  const { data: partnerAdminAccess } = useGetPartnerAdminAccessQuery();
  const [show, setShow] = useState(false);
  const { goTo } = useNavegate();

  const hasPending = (partnerAdminAccess?.count ?? 0) > 0;
  const [wasDismissed, setWasDismissed] = useState(false);

  useEffect(() => {
    if (!hasPending) {
      setShow(false);
      setWasDismissed(false); // reset when there are no pending items
      return;
    }
    if (!wasDismissed) setShow(true);
  }, [hasPending, wasDismissed]);

  const handleRemove = useCallback(() => {
    setWasDismissed(true);
    setShow(false);
  }, []);

  const handleClickDetails = () => {
    goTo('/account/users');
  };

  return (
    <Alert
      appearance="primary"
      show={show}
      title={t('title')}
      onRemove={handleRemove}
    >
      {partnerAdminAccess && partnerAdminAccess?.count === 1
        ? t('content', {
            partnerName:
              partnerAdminAccess?.permissionsWithPartner?.[0]?.partnerName,
            reviewLimitDate:
              partnerAdminAccess?.permissionsWithPartner?.[0]?.expiredAt,
            interpolation: { escapeValue: false },
          })
        : t('content_for_multiple_partners', {
            count: partnerAdminAccess?.count,
            reviewLimitDate:
              partnerAdminAccess?.permissionsWithPartner?.[0]?.expiredAt,
            interpolation: { escapeValue: false },
          })}
      {showDetailsButton && (
        <Button onClick={handleClickDetails}>{t('detailsButton')}</Button>
      )}
    </Alert>
  );
}

export default PartnerAdminAlert;
